import {
  Box,
  Stack,
  Flex,
  VStack,
  Text,
  HStack,
  chakra,
  useBreakpointValue,
} from "@chakra-ui/react";
import { Element } from "@prismicio/react";
import { FC, Fragment } from "react";
import Renderer from "src/lib/renderer";
import { heading1, heading4 } from "src/lib/renderer/elements/headings";
import { BlockContainer, IconBox } from "src/atoms";
import { PrismicLink } from "src/atoms";
import { SvgOrImg } from "src/components/SvgOrImg";
import paragraph from "src/lib/renderer/elements/paragraph";

const BlockSecurity: FC<Gatsby.PageBlockSecurityFragment> = ({
  primary,
  items,
}) => {
  if (!primary) throw Error();

  const { content, buttons, background_color: backgroundColor } = primary;
  const hasDarkBg = backgroundColor === "blue" || backgroundColor === "green";
  const size = useBreakpointValue({ base: 16, md: 24 }) || 16;

  return (
    <BlockContainer>
      <Stack
        direction={{ base: "column", md: "row" }}
        borderRadius="intermediate"
        bg={hasDarkBg ? "blue-0" : "grey-3"}
        w="full"
        p={{ base: "space-24", md: "space-80" }}
        spacing={{ base: "space-16", md: "space-80" }}
        align="center"
        justifyContent="space-between"
      >
        <VStack
          textAlign={{ base: "center", md: "left" }}
          spacing={{ base: "space-16", md: "space-24" }}
          align={{ base: "center", md: "flex-start" }}
        >
          <Renderer
            overrides={{
              [Element.heading2]: (args) =>
                heading1({
                  ...args,
                  overrideProps: {
                    variant: "heading-1",
                    marginBottom: 0,
                    color: hasDarkBg ? "white" : "text-heading"
                  },
                }),
              [Element.heading3]: (args) =>
                heading4({
                  ...args,
                  overrideProps: {
                    variant: "heading-4",
                    marginBottom: 0,
                    color: hasDarkBg ? "white" : "text-heading"
                  },
                }),
              [Element.paragraph]: (args: any) =>
                paragraph({
                  ...args,
                  overrideProps: {
                    color: hasDarkBg ? "white" : "text-primary"
                  },
                }),
            }}
            field={content}
          />
          <Box
            display={{ base: "none", md: "block" }}
            sx={{
              p: {
                _last: {
                  marginBottom: 0,
                },

              },
              ".chakra-button": {
                w: { base: "full", md: "auto" },
                ...(hasDarkBg && {
                  color: "white",
                  _hover: {
                    color: "prosperity-stone-50",
                  },
                }),
              },
            }}
          >
            <Renderer field={buttons} />
          </Box>
        </VStack>
        <VStack w="full" maxW={{ md: "526px" }} >
          {items?.map((item) => {
            if (!item) return null;

            const isLink = Boolean(item?.link?.url);

            return (
              <HStack
                bg="white"
                key={item.label}
                p="space-8"
                borderRadius="intermediate"
                w="full"
                spacing="space-8"
                position="relative"
                sx={{
                  ":hover": {
                    ".element-cta-icon": {
                      transform: (theme) => `translateX(${theme.space[1]})`,
                    },
                  },
                }}
              >
                <IconBox
                  w={{ base: "40px", md: "56px" }}
                  h={{ base: "40px", md: "56px" }}
                  bg="grey-3"
                  borderRadius={{ base: "12px", md: "md" }}
                  justifyContent="center"
                  alignItems="center"
                >
                  {item?.icon?.document?.__typename === "PrismicElementImage"
                    ? <SvgOrImg
                      additionalBoxProps={{
                        maxW: { base: "16px", md: "24px" },
                        maxH: { base: "16px", md: "24px" }
                      }}
                      imageFile={item?.icon?.document?.data?.image_file}
                      isBox={true}
                      size={size}
                    />
                    : null
                  }
                </IconBox>
                <Flex w="full" alignItems="center">
                  <Text as="div" textAlign="left" pr="space-8" variant="body-sm-reg">
                    {item.label}
                  </Text>
                  {isLink ? (
                    <Fragment>
                      <chakra.span
                        transition="transform .2s ease-out"
                        w="16px"
                        className="element-cta-icon"
                      >
                        →
                      </chakra.span>
                      <Box
                        as={PrismicLink}
                        link={item.link!}
                        position="absolute"
                        w="full"
                        h="full"
                        top="0"
                        left="0"
                      />
                    </Fragment>
                  ) : null}
                </Flex>
              </HStack>
            );
          })}
        </VStack>
        <Box
          display={{ md: "none" }}
          w="full"
          sx={{
            p: {
              _last: {
                marginBottom: 0,
              },
            },
            ".chakra-button": {
              w: { base: "full", md: "auto" },
              ...(hasDarkBg && {
                color: "white",
                _hover: {
                  color: "prosperity-stone-50",
                },
              }),
            },
          }}
        >
          <Renderer field={buttons} />
        </Box>
      </Stack>
    </BlockContainer>
  );
};

export default BlockSecurity;
